import type { StockLevel } from '@/domains/products/types/consts/StockLevel.consts'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { InventoryItemModel } from '@/domains/products/types/models/InventoryItemModel.types'
import type {
  StockBreakdownRow,
  StockLevelBadge,
  StockSummary
} from '@/domains/products/utils/stock-level/stockLevel.types'
import type { IndexProductVariant } from '@/domains/variants/types/models/IndexProductVariant.types'
import type { DisplayUnit } from '@/utils/unit-util/UnitUtil.types'

import {
  resolveStatusLabel,
  resolveStatusSeverity
} from '@/components/atoms/badges/twn-status-indicator/TwnStatusIndicator.utils'
import { useProductVariantHelper } from '@/domains/products/composables/use-product-variant-helper/useProductVariantHelper'
import {
  STOCK_LEVEL,
  STOCK_LEVEL_LABELS,
  STOCK_LEVEL_PRIORITY,
  STOCK_LEVEL_SEVERITY
} from '@/domains/products/types/consts/StockLevel.consts'
import { PLACEHOLDERS } from '@/types/const/placeholders'
import { formatStock } from '@/utils/unit-util/unitUtil'

/**
 * Stock read the way the backend reads it.
 *
 * @remarks
 * The rule lives twice — here, and in
 * `Domain\Inventory\QueryBuilders\InventoryItemQueryBuilder::belowReorderPoint()`, which is
 * the authority. `__tests__/stockLevel.test.ts` encodes that predicate case by case, so if
 * the backend line moves, that suite is what has to change first.
 *
 * Plain functions rather than a composable: the products table calls them once per row inside
 * a template loop. Mirrors `domains/inventory/utils/replenishment-display`.
 */

/**
 * What the row says is on the shelf.
 *
 * @remarks
 * `stock_levels.sellable` is the answer wherever the payload carries one. It is the backend's
 * own word for this figure and it has exactly one spelling.
 *
 * The two flat keys behind it are the fallback, for a row a form is still assembling.
 * `InventoryItemModel` carries `available_qty` and `available_quantity`, both optional, because
 * two backend DTOs spell it differently — the products payload sends the short form,
 * replenishment the long one. Read both here and nowhere else: picking the wrong key yields
 * `undefined`, which falls to zero and reads as a real quantity rather than as an error — "Out
 * of Stock" at a managed location, and a silent zero in the popover at any other.
 *
 * Negatives are not clamped. The backend does not clamp them, and -3 is out of stock either way.
 */
const availableQuantity = (item: InventoryItemModel): number =>
  item.stock_levels?.sellable ?? item.available_qty ?? item.available_quantity ?? 0

/**
 * The verdict for one variant at one location.
 *
 * @remarks
 * An unmanaged location never raises an alarm, whatever its quantity. The backend gates
 * replenishment the same way — a null point means the merchant never asked — and a tenant with
 * 24 locations holds an inventory row for every one of them whether it stocks the product or
 * not. Letting an empty shelf nobody claimed shout would paint most of the screen: on the
 * `gauci` tenant one product sat at 25 against a point of 20 in the shop it is managed in, and
 * read "Out of Stock" because 21 shops it was never stocked in held zero.
 *
 * Below the point is `<=`, matching the backend: stock resting exactly on the line is already
 * asking for an order.
 */
const stockLevelForItem = (item: InventoryItemModel): StockLevel => {
  if (item.reorder_point == null) return STOCK_LEVEL.NO_REORDER_POINT

  const available = availableQuantity(item)

  if (available <= 0) return STOCK_LEVEL.OUT_OF_STOCK

  return available <= item.reorder_point ? STOCK_LEVEL.BELOW_REORDER_POINT : STOCK_LEVEL.IN_STOCK
}

/**
 * The worst level among the rows in scope, or null when there is nothing to judge.
 *
 * @example worstStockLevel(rows) // 'below_reorder_point'
 */
const worstStockLevel = (rows: readonly StockBreakdownRow[]): StockLevel | null => {
  let worst: StockLevel | null = null
  let worstRank: number = STOCK_LEVEL_PRIORITY.length

  rows.forEach(row => {
    const rank = STOCK_LEVEL_PRIORITY.indexOf(row.level)

    if (rank < worstRank) {
      worst = row.level
      worstRank = rank
    }
  })

  return worst
}

/**
 * A product carries variants; a variant does not.
 *
 * @remarks
 * A hand-written guard rather than `'variants' in context`: the property is optional on
 * `IndexProduct`, so `in` leaves the union unnarrowed and the false branch untyped.
 */
const isProduct = (context: IndexProduct | IndexProductVariant): context is IndexProduct => 'variants' in context

/** The two states that ask the merchant for something. */
const isShort = (level: StockLevel): boolean =>
  level === STOCK_LEVEL.BELOW_REORDER_POINT || level === STOCK_LEVEL.OUT_OF_STOCK

/**
 * A variant holds stock only when the merchant asked us to track it.
 *
 * @remarks
 * `=== true` rather than a truthiness check, because the field is nullable and a null has to
 * read as the column default it stands for, which is false.
 */
const tracksInventory = (variant: IndexProductVariant): boolean => variant.is_tracking_inventory === true

/** The variants to ask about — a product's own, or the variant itself. */
const variantsOf = (context: IndexProduct | IndexProductVariant): IndexProductVariant[] =>
  isProduct(context) ? (context.variants ?? []) : [context]

/**
 * Keep the locations the screen is looking at.
 *
 * @remarks
 * An item whose location did not load is dropped while a scope is active. It cannot be shown to
 * be in scope, and judging it anyway would let an out-of-scope shop colour the badge.
 */
const inScope = (item: InventoryItemModel, locationUlids?: readonly string[]): boolean => {
  if (!locationUlids?.length) return true

  return !!item.location?.ulid && locationUlids.includes(item.location.ulid)
}

const { getVariantDisplayName } = useProductVariantHelper()

const rowsForVariant = (variant: IndexProductVariant, locationUlids?: readonly string[]): StockBreakdownRow[] => {
  const variantLabel = getVariantDisplayName(variant, false)

  return (variant.inventory_items ?? [])
    .filter(item => inScope(item, locationUlids))
    .map(item => {
      const level = stockLevelForItem(item)

      return {
        key: `${variant.id}:${item.location?.ulid ?? item.id}`,
        variantLabel,
        locationName: item.location?.name ?? PLACEHOLDERS.NA,
        available: availableQuantity(item),
        pricingUnit: variant.pricing_unit ?? null,
        reorderPoint: item.reorder_point ?? null,
        level,
        isShort: isShort(level)
      }
    })
}

/**
 * One row per (variant, location) pair the screen is judging.
 *
 * @remarks
 * Empty says only that there is no row to list. What that means is `stockSummary`'s to decide,
 * because "nothing tracks stock" and "tracks stock, held nowhere" are different answers.
 */
const stockBreakdown = (
  context: IndexProduct | IndexProductVariant,
  locationUlids?: readonly string[]
): StockBreakdownRow[] =>
  variantsOf(context)
    .filter(tracksInventory)
    .flatMap(variant => rowsForVariant(variant, locationUlids))

/**
 * What the badge shows, and what the popover lists.
 *
 * @remarks
 * Every silence gets a name, because one blank cell cannot mean three things and still be
 * read. `not_tracked` says a sale moves no stock here. `not_stocked` says it would, but no
 * location holds a record — usually a gap rather than a decision, since creating a location
 * creates no inventory rows.
 *
 * `level` is null only when there is no variant at all to ask about. That is the one case
 * with genuinely nothing to say, and the only one that renders no badge.
 */
const stockSummary = (context: IndexProduct | IndexProductVariant, locationUlids?: readonly string[]): StockSummary => {
  const variants = variantsOf(context)

  if (variants.length === 0) {
    return { level: null, rows: [] }
  }

  const tracking = variants.filter(tracksInventory)

  if (tracking.length === 0) {
    return { level: STOCK_LEVEL.NOT_TRACKED, rows: [] }
  }

  const rows = tracking.flatMap(variant => rowsForVariant(variant, locationUlids))

  return {
    level: worstStockLevel(rows) ?? STOCK_LEVEL.NOT_STOCKED,
    rows
  }
}

/**
 * What the badge says about a summary.
 *
 * @remarks
 * One row carries its own number, because there is exactly one to state. That number reads in
 * `unit`, the stock unit of the row's pricing unit, which the caller resolves. Null where the
 * summary has no level, which is the one case that draws no badge.
 */
const stockLevelBadge = (summary: StockSummary, unit?: DisplayUnit): StockLevelBadge | null => {
  if (!summary.level) return null

  const name = resolveStatusLabel(summary.level, STOCK_LEVEL_LABELS)
  const [only] = summary.rows

  return {
    label: only && summary.rows.length === 1 ? `${name} · ${formatStock({ baseCount: only.available, unit })}` : name,
    severity: resolveStatusSeverity(summary.level, STOCK_LEVEL_SEVERITY, 'contrast')
  }
}

export {
  availableQuantity,
  stockBreakdown,
  stockLevelBadge,
  stockLevelForItem,
  stockSummary,
  tracksInventory,
  worstStockLevel
}
